// Function index:
//   getJson<T>(url)         — GET → JSON.
//   postJson<T>(url, body)  — POST application/json; void for 204.
//   putJson<T>(url, body)   — PUT application/json.
//   HttpError              — Error carrying the response status.
//   asError(res)            — turn a non-2xx response into an HttpError.
//
// Cookies (session) are sent automatically same-origin (the Vite dev proxy
// makes /api/* go to the Fastify server).

export async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await asError(res);
  return res.status === 204 ? (undefined as T) : res.json();
}

export async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw await asError(res);
  return res.json();
}

export async function putJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await asError(res);
  return res.json();
}

// Carries the HTTP status alongside the message. Callers that need to tell
// "this feature isn't configured" (503) from "the request broke" (500) cannot
// do it from message text alone — billing is the live example.
export interface HttpError extends Error { status: number }

export async function asError(res: Response): Promise<HttpError> {
  let message = `HTTP ${res.status}`;
  try {
    const body = await res.json();
    if (body?.error) message = body.error;
  } catch { /* non-JSON body — keep the status line */ }
  const err = new Error(message) as HttpError;
  err.status = res.status;
  return err;
}
